import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import {
  CreditCard,
  TrendingUp,
  AlertTriangle,
  DollarSign,
  Calendar,
  RefreshCw,
  Building2,
  GitBranch,
  Users,
  BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BillingPage({ user, onLogout }) {
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [usage, setUsage] = useState(null);
  const [recentScans, setRecentScans] = useState([]);

  const fetchBillingData = useCallback(async () => {
    if (!user?.organization_id) {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [orgRes, usageRes, scansRes] = await Promise.all([
        fetch(`${API_URL}/api/organizations/${user.organization_id}`, { headers }),
        fetch(`${API_URL}/api/organizations/${user.organization_id}/usage?period=monthly`, { headers }),
        fetch(`${API_URL}/api/scans?limit=10`, { headers })
      ]);

      if (orgRes.ok) setOrganization(await orgRes.json());
      if (usageRes.ok) setUsage(await usageRes.json());
      if (scansRes.ok) setRecentScans(await scansRes.json());
    } catch (error) {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, [user?.organization_id]);

  useEffect(() => {
    fetchBillingData();
  }, [fetchBillingData]);

  const getCreditsPercentage = () => {
    if (!organization) return 0;
    const { prepaid_credits, remaining_credits } = organization;
    if (prepaid_credits === 0) return 0;
    return (remaining_credits / prepaid_credits) * 100;
  };

  const getCreditWarningLevel = () => {
    const percentage = getCreditsPercentage();
    if (percentage <= 10) return 'critical';
    if (percentage <= 25) return 'warning';
    return 'healthy';
  };

  // Super admin doesn't have an organization - show different view
  if (user?.role === 'super_admin') {
    return (
      <Layout user={user} onLogout={onLogout} activePage="billing">
        <div className="max-w-4xl mx-auto">
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Super Admin Account
              </CardTitle>
              <CardDescription>
                Super admin accounts don&apos;t have credit limits. Manage organization billing from the Organizations page.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => window.location.href = '/organizations'}>
                Go to Organizations
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // User without organization
  if (!user?.organization_id) {
    return (
      <Layout user={user} onLogout={onLogout} activePage="billing">
        <div className="max-w-4xl mx-auto">
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                No Organization
              </CardTitle>
              <CardDescription>
                Your account is not associated with an organization. Contact your administrator.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} activePage="billing">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  const warningLevel = getCreditWarningLevel();

  return (
    <Layout user={user} onLogout={onLogout} activePage="billing">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Billing & Usage</h1>
            <p className="text-slate-500 mt-1">
              {organization?.name} - Credit usage and billing information
            </p>
          </div>
          <Button variant="outline" onClick={fetchBillingData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Low Credits Alert */}
        {warningLevel !== 'healthy' && (
          <Alert variant={warningLevel === 'critical' ? 'destructive' : 'default'} 
                 className={warningLevel === 'critical' ? 'border-red-500 bg-red-50' : 'border-amber-500 bg-amber-50'}>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>
              {warningLevel === 'critical' ? 'Credits Nearly Depleted!' : 'Low Credits Warning'}
            </AlertTitle>
            <AlertDescription>
              {warningLevel === 'critical' 
                ? `Only ${organization?.remaining_credits?.toLocaleString()} credits remaining. Contact your administrator to add more credits.`
                : `You have ${organization?.remaining_credits?.toLocaleString()} credits remaining (${getCreditsPercentage().toFixed(0)}%). Consider requesting more credits soon.`
              }
            </AlertDescription>
          </Alert>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Remaining Credits</p>
                  <p className={`text-2xl font-bold ${
                    warningLevel === 'critical' ? 'text-red-600' : 
                    warningLevel === 'warning' ? 'text-amber-600' : 'text-slate-900'
                  }`}>
                    {organization?.remaining_credits?.toLocaleString() || 0}
                  </p>
                </div>
                <CreditCard className={`w-8 h-8 ${
                  warningLevel === 'critical' ? 'text-red-500' : 
                  warningLevel === 'warning' ? 'text-amber-500' : 'text-blue-500'
                }`} />
              </div>
              <Progress 
                value={getCreditsPercentage()} 
                className={`mt-3 h-2 ${
                  warningLevel === 'critical' ? '[&>div]:bg-red-500' : 
                  warningLevel === 'warning' ? '[&>div]:bg-amber-500' : ''
                }`} 
              />
              <p className="text-xs text-slate-500 mt-2">
                of {organization?.prepaid_credits?.toLocaleString()} total
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Used This Period</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {usage?.total_scans?.toLocaleString() || 0}
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-500" />
              </div>
              <p className="text-xs text-slate-500 mt-3">
                scans this {organization?.billing_cycle || 'month'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Price per Scan</p>
                  <p className="text-2xl font-bold text-slate-900">
                    ${organization?.pricing_per_scan?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-indigo-500" />
              </div>
              <p className="text-xs text-slate-500 mt-3">
                USD per screening
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Period Cost</p>
                  <p className="text-2xl font-bold text-slate-900">
                    ${usage?.cost_usd?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <BarChart3 className="w-8 h-8 text-purple-500" />
              </div>
              <p className="text-xs text-slate-500 mt-3">
                {organization?.billing_cycle || 'monthly'} charges
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Organization Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Organization Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between py-2 border-b">
                <span className="text-slate-500">Name</span>
                <span className="font-medium">{organization?.name}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-slate-500">Status</span>
                <Badge variant={organization?.status === 'active' ? 'default' : 'destructive'}>
                  {organization?.status}
                </Badge>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-slate-500">Billing Cycle</span>
                <span className="font-medium capitalize">{organization?.billing_cycle}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-slate-500">Total Users</span>
                <span className="font-medium flex items-center">
                  <Users className="w-4 h-4 mr-1 text-slate-400" />
                  {organization?.total_users}
                </span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-slate-500">Total Branches</span>
                <span className="font-medium flex items-center">
                  <GitBranch className="w-4 h-4 mr-1 text-slate-400" />
                  {organization?.total_branches}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Usage by Branch */}
          {usage?.breakdown_by_branch && Object.keys(usage.breakdown_by_branch).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GitBranch className="w-5 h-5" />
                  Usage by Branch
                </CardTitle>
                <CardDescription>
                  Scans breakdown by branch this period
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(usage.breakdown_by_branch).map(([branch, count]) => (
                    <div key={branch} className="flex items-center justify-between py-2 border-b last:border-0">
                      <span className="text-slate-600">{branch}</span>
                      <span className="font-medium">{count.toLocaleString()} scans</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Recent Scans */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Recent Scans
            </CardTitle>
            <CardDescription>Your most recent screening activity</CardDescription>
          </CardHeader>
          <CardContent>
            {recentScans.length === 0 ? (
              <p className="text-center py-8 text-slate-500">No scans yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Risk Level</TableHead>
                    <TableHead>Matches</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentScans.map((scan) => (
                    <TableRow key={scan.id}>
                      <TableCell className="font-medium">{scan.query_name}</TableCell>
                      <TableCell>
                        <Badge variant={
                          scan.risk_level === 'critical' ? 'destructive' :
                          scan.risk_level === 'high' ? 'destructive' :
                          scan.risk_level === 'medium' ? 'secondary' : 'outline'
                        }>
                          {scan.risk_level}
                        </Badge>
                      </TableCell>
                      <TableCell>{scan.matches?.length || 0}</TableCell>
                      <TableCell className="text-slate-500">
                        {new Date(scan.scan_date).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
